import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiPatch, apiPost } from '../api/client';
import type { LeadActivity, LeadScore, LeadWithIntelligence } from '../api/types';
import { formatDate, timeAgo, stageLabel } from '../lib/format';
import styles from './LeadDetailPage.module.css';

const TRANSITIONS: Record<string, string[]> = {
  new: ['researching', 'outreach', 'lost', 'dormant'],
  researching: ['outreach', 'new', 'lost', 'dormant'],
  outreach: ['conversation', 'new', 'researching', 'lost', 'dormant'],
  conversation: ['diagnostic', 'outreach', 'lost', 'dormant'],
  diagnostic: ['proposal', 'conversation', 'lost', 'dormant'],
  proposal: ['negotiation', 'diagnostic', 'lost', 'dormant'],
  negotiation: ['won', 'lost', 'proposal', 'dormant'],
  won: [],
  lost: [],
  dormant: ['new', 'researching', 'outreach', 'conversation', 'diagnostic', 'proposal', 'negotiation'],
};

const ACTIVITY_ICONS: Record<string, string> = {
  note: 'Nota',
  email_sent: 'Env.',
  email_opened: 'Vis.',
  email_replied: 'Resp.',
  call: 'Tel.',
  meeting: 'Reun.',
  score_change: 'Score',
  stage_change: 'Etapa',
  llm_action: 'IA',
  enrichment: 'Enr.',
};

const RESPONSE_LABELS: Record<string, string> = {
  none: 'Sin respuesta',
  opened: 'Abierto',
  replied: 'Respondido',
  meeting_booked: 'Reunion agendada',
  declined: 'Rechazado',
};

const FIELD_LABELS: Record<string, string> = {
  companyName: 'Empresa',
  contactName: 'Nombre',
  contactTitle: 'Titulo',
  contactEmail: 'Email',
  contactPhone: 'Telefono',
  websiteUrl: 'Website',
  sector: 'Sector',
  subsector: 'Subsector',
  tier: 'Tier',
  region: 'Region',
  painPoint: 'Pain Point',
  commercialAngle: 'Angulo Comercial',
  uspVerified: 'USP Verificado',
  productStar: 'Producto Estrella',
  websiteQuality: 'Calidad del Website',
  taxId: 'NIF / CIF / NIE',
  taxIdType: 'Tipo fiscal',
  legalName: 'Razon Social',
  addressLine1: 'Direccion Fiscal',
  city: 'Ciudad',
  postalCode: 'Codigo Postal',
  country: 'Pais',
};

const STAGE_COLORS: Record<string, string> = {
  new: '#6366f1',
  researching: '#8b5cf6',
  outreach: '#3b82f6',
  conversation: '#06b6d4',
  diagnostic: '#14b8a6',
  proposal: '#f59e0b',
  negotiation: '#f97316',
  won: '#0f7a43',
  lost: '#dc2626',
  dormant: '#9ca3af',
};

function priorityBadge(score: number | null | undefined): string {
  if (score == null) return '';
  if (score >= 80) return 'Prioridad Alta';
  if (score >= 60) return 'Media';
  if (score >= 40) return 'Baja';
  return '';
}

function scoreBarColor(score: number): string {
  if (score >= 70) return 'var(--color-success)';
  if (score >= 50) return 'var(--priority-alta)';
  if (score >= 30) return 'var(--priority-media)';
  return 'var(--priority-baja)';
}

function scoreClass(score: number | null | undefined): string {
  if (score == null) return styles.scoreMin;
  if (score >= 70) return styles.scoreHigh;
  if (score >= 50) return styles.scoreMid;
  if (score >= 30) return styles.scoreLow;
  return styles.scoreMin;
}

function detectTaxIdType(value: string): string {
  const taxId = value.trim().toUpperCase();
  if (!taxId) return '';
  const first = taxId.charAt(0);
  if (first === 'X' || first === 'Y' || first === 'Z') return 'NIE';
  if (first >= '0' && first <= '9') return 'NIF';
  if ('ABCDEFGHJKLMNPQRSUVW'.includes(first)) return 'CIF';
  return '';
}

interface EnrichResult {
  confidence?: string;
  [key: string]: unknown;
}

interface OutreachResult {
  templateUsed?: string;
  subject?: string;
  body?: string;
  personalizationNotes?: string;
}

interface FollowupResult {
  action?: string;
  reasoning?: string;
  urgency?: string;
  suggestedStage?: string;
}

type LlmResult =
  | { type: 'enrich'; data: EnrichResult }
  | { type: 'outreach'; data: OutreachResult }
  | { type: 'followup'; data: FollowupResult };

interface PageState {
  loading: boolean;
  error: string | null;
  lead: LeadWithIntelligence | null;
  activities: LeadActivity[];
  scores: LeadScore[];
}

interface EditState {
  editing: boolean;
  form: Record<string, string>;
}

interface TransferState {
  open: boolean;
  step: number;
  fiscal: {
    taxId: string;
    taxIdType: string;
    legalName: string;
    addressLine1: string;
    city: string;
    postalCode: string;
    country: string;
  };
  result: string | null;
  loading: boolean;
}

interface LlmState {
  result: LlmResult | null;
  loading: string | null;
}

function useLeadDetail() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState<PageState>({ loading: true, error: null, lead: null, activities: [], scores: [] });
  const [edit, setEdit] = useState<EditState>({ editing: false, form: {} });
  const [transfer, setTransfer] = useState<TransferState>({
    open: false,
    step: 1,
    fiscal: { taxId: '', taxIdType: '', legalName: '', addressLine1: '', city: '', postalCode: '', country: 'ES' },
    result: null,
    loading: false,
  });
  const [llm, setLlm] = useState<LlmState>({ result: null, loading: null });

  const loadLead = async () => {
    if (!id) return;
    setPage((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await apiGet(`/api/leads/${id}`) as {
        lead: LeadWithIntelligence;
        activities: LeadActivity[];
        scores: LeadScore[];
      };
      setPage({ loading: false, error: null, lead: res.lead, activities: res.activities || [], scores: res.scores || [] });
      setEdit((prev) => ({ ...prev, form: {} }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setPage((prev) => ({ ...prev, loading: false, error: message }));
    }
  };

  useEffect(() => {
    loadLead();
  }, [id]);

  const startEdit = () => {
    if (!page.lead) return;
    const form: Record<string, string> = {};
    const fields = [
      'companyName','contactName','contactTitle','contactEmail','contactPhone',
      'websiteUrl','sector','subsector','tier','region','fitScore','readinessScore',
      'painPoint','commercialAngle','uspVerified','productStar','responseStatus','notes',
      'taxId','taxIdType','legalName','addressLine1','city','postalCode','country',
    ];
    for (const f of fields) {
      const v = (page.lead as unknown as Record<string, unknown>)[f];
      form[f] = v != null ? String(v) : '';
    }
    setEdit((prev) => ({ ...prev, form, editing: true }));
  };

  const cancelEdit = () => {
    setEdit((prev) => ({ ...prev, editing: false, form: {} }));
  };

  const saveEdit = async () => {
    if (!id || !page.lead) return;
    const payload: Record<string, unknown> = {};
    const fields = [
      'companyName','contactName','contactTitle','contactEmail','contactPhone',
      'websiteUrl','sector','subsector','tier','region','fitScore','readinessScore',
      'painPoint','commercialAngle','uspVerified','productStar','responseStatus','notes',
      'taxId','taxIdType','legalName','addressLine1','city','postalCode','country',
    ];
    for (const f of fields) {
      const val = edit.form[f];
      const orig = (page.lead as unknown as Record<string, unknown>)[f];
      if (f === 'fitScore' && val) {
        payload[f] = parseInt(val, 10);
      } else if (val !== '' && val != null) {
        payload[f] = val;
      } else if (orig != null && orig !== '') {
        payload[f] = '';
      }
    }
    if (Object.keys(payload).length === 0) {
      cancelEdit();
      return;
    }
    try {
      await apiPatch(`/api/leads/${id}`, payload);
      cancelEdit();
      loadLead();
    } catch (err) {
      alert('Error al guardar');
    }
  };

  const transitionStage = async (stage: string) => {
    if (!id) return;
    try {
      await apiPost(`/api/leads/${id}/transition`, { toStage: stage, note: '' });
      loadLead();
    } catch (err) {
      alert('Error al cambiar stage');
    }
  };

  const openTransfer = () => {
    if (!page.lead) return;
    setTransfer((prev) => ({
      ...prev,
      open: true,
      step: 1,
      result: null,
      fiscal: {
        taxId: page.lead!.taxId || '',
        taxIdType: page.lead!.taxIdType || '',
        legalName: page.lead!.legalName || page.lead!.companyName || '',
        addressLine1: page.lead!.addressLine1 || page.lead!.address || '',
        city: page.lead!.city || page.lead!.region || '',
        postalCode: page.lead!.postalCode || '',
        country: page.lead!.country || 'ES',
      },
    }));
  };

  const closeTransfer = () => {
    setTransfer((prev) => ({ ...prev, open: false, result: null }));
  };

  const submitTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setTransfer((prev) => ({ ...prev, loading: true, result: 'Transfiriendo lead...' }));
    const fiscal = {
      taxId: transfer.fiscal.taxId.trim().toUpperCase() || null,
      taxIdType: detectTaxIdType(transfer.fiscal.taxId) || null,
      legalName: transfer.fiscal.legalName.trim() || null,
      addressLine1: transfer.fiscal.addressLine1.trim() || null,
      city: transfer.fiscal.city.trim() || null,
      postalCode: transfer.fiscal.postalCode.trim() || null,
      country: transfer.fiscal.country.trim().toUpperCase() || 'ES',
    };
    try {
      const res = await apiPost(`/api/leads/${id}/transfer`, { fiscal }) as { clientId?: string; error?: string };
      setTransfer((prev) => ({ ...prev, result: `Cliente creado. ID: ${res.clientId || ''}` }));
      loadLead();
    } catch (err) {
      setTransfer((prev) => ({ ...prev, result: 'Error al transferir lead.' }));
    } finally {
      setTransfer((prev) => ({ ...prev, loading: false }));
    }
  };

  const runEnrich = async () => {
    if (!id) return;
    setLlm((prev) => ({ ...prev, loading: 'enrich' }));
    try {
      const res = await apiPost(`/api/llm/enrich/${id}`, {}) as { success: boolean; data?: EnrichResult; error?: string };
      if (res.success && res.data) {
        const data = res.data;
        setLlm((prev) => ({ ...prev, result: { type: 'enrich', data } }));
      }
    } catch (_) {
      alert('Error al enriquecer');
    } finally {
      setLlm((prev) => ({ ...prev, loading: null }));
    }
  };

  const runOutreach = async () => {
    if (!id) return;
    setLlm((prev) => ({ ...prev, loading: 'outreach' }));
    try {
      const res = await apiPost(`/api/llm/generate-outreach/${id}`, {}) as { success: boolean; data?: OutreachResult; error?: string };
      if (res.success && res.data) {
        const data = res.data;
        setLlm((prev) => ({ ...prev, result: { type: 'outreach', data } }));
      }
    } catch (_) {
      alert('Error al generar outreach');
    } finally {
      setLlm((prev) => ({ ...prev, loading: null }));
    }
  };

  const runFollowup = async () => {
    if (!id) return;
    setLlm((prev) => ({ ...prev, loading: 'followup' }));
    try {
      const res = await apiPost(`/api/llm/suggest-followup/${id}`, {}) as { success: boolean; data?: FollowupResult; error?: string };
      if (res.success && res.data) {
        const data = res.data;
        setLlm((prev) => ({ ...prev, result: { type: 'followup', data } }));
      }
    } catch (_) {
      alert('Error al generar follow-up');
    } finally {
      setLlm((prev) => ({ ...prev, loading: null }));
    }
  };

  const applyEnrichField = async (field: string, value: string) => {
    if (!id) return;
    try {
      await apiPost('/api/llm/apply-suggestion', { leadId: id, fields: { [field]: value } });
      loadLead();
    } catch (_) {
      alert('Error al aplicar campo');
    }
  };

  const applyAllEnrich = async () => {
    if (!id || !llm.result || llm.result.type !== 'enrich') return;
    const data = llm.result.data;
    const fields: Record<string, unknown> = {};
    const enrichable = [
      'companyName','contactName','contactTitle','contactEmail','sector','subsector','region',
      'painPoint','commercialAngle','uspVerified','productStar','websiteQuality',
      'taxId','taxIdType','legalName','addressLine1','city','postalCode','country',
    ];
    for (const f of enrichable) {
      const v = data[f];
      if (v != null && v !== '') fields[f] = v;
    }
    if (Object.keys(fields).length === 0) return;
    try {
      await apiPost('/api/llm/apply-suggestion', { leadId: id, fields });
      setLlm((prev) => ({ ...prev, result: null }));
      loadLead();
    } catch (_) {
      alert('Error al aplicar sugerencias');
    }
  };

  const copyOutreach = async () => {
    if (!llm.result || llm.result.type !== 'outreach') return;
    const text = `Subject: ${llm.result.data.subject || ''}\n\n${llm.result.data.body || ''}`;
    try {
      await navigator.clipboard.writeText(text);
      alert('Email copiado al portapapeles');
    } catch (_) {
      alert('Error al copiar');
    }
  };

  const editInput = (name: string, value: string) => {
    setEdit((prev) => ({ ...prev, form: { ...prev.form, [name]: value } }));
  };

  const transitions = useMemo(() => {
    if (!page.lead) return [];
    return TRANSITIONS[page.lead.stage] || [];
  }, [page.lead?.stage]);

  const closeLlmResult = () => {
    setLlm((prev) => ({ ...prev, result: null }));
  };

  const setTransferStep = (step: number) => {
    setTransfer((prev) => ({ ...prev, step }));
  };

  const updateFiscal = (next: Partial<TransferState['fiscal']>) => {
    setTransfer((prev) => ({ ...prev, fiscal: { ...prev.fiscal, ...next } }));
  };

  return {
    page, edit, transfer, llm,
    startEdit, cancelEdit, saveEdit, transitionStage,
    openTransfer, closeTransfer, submitTransfer,
    runEnrich, runOutreach, runFollowup,
    applyEnrichField, applyAllEnrich, copyOutreach,
    editInput, transitions,
    closeLlmResult, setTransferStep, updateFiscal,
  };
}

export default function LeadDetailPage() {
  const {
    page, edit, transfer, llm,
    startEdit, cancelEdit, saveEdit, transitionStage,
    openTransfer, closeTransfer, submitTransfer,
    runEnrich, runOutreach, runFollowup,
    applyEnrichField, applyAllEnrich, copyOutreach,
    editInput, transitions,
    closeLlmResult, setTransferStep, updateFiscal,
  } = useLeadDetail();

  if (page.loading) {
    return (
      <div className={styles.emptyState}>
        <div className="spinner" />
        <p>Cargando lead&hellip;</p>
      </div>
    );
  }

  if (page.error || !page.lead) {
    return (
      <div className={styles.emptyState}>
        <h3>{page.error ? 'Error al cargar' : 'Lead no encontrado'}</h3>
        <p>{page.error || 'No existe un lead con este ID.'}</p>
        <Link to="/pipeline" className={`${styles.btn} ${styles.btnPrimary}`}>
          Volver al Pipeline
        </Link>
      </div>
    );
  }

  const lead = page.lead;
  const compositeScore = lead.compositeScore ?? 0;
  const priority = priorityBadge(lead.compositeScore);

  return (
    <>
      {/* Masthead */}
      <div className={styles.masthead}>
        <div className={styles.mastheadWrap}>
          <p className={styles.kicker}>
            <Link to="/pipeline" className={styles.backLink}>&larr; Pipeline</Link>
            <span className={styles.kickerSep} aria-hidden="true">&middot;</span>
            <span className={styles.kickerLabel}>Expediente</span>
          </p>
          <h1 className={styles.head}>
            {lead.companyName}
            {priority && <span className={styles.priorityBadge}>{priority}</span>}
          </h1>
        </div>
        <div className={styles.actions}>
          {lead.crmClientId && (
            <a
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
              href={`https://crm.grandeandgordo.com/admin/clients/detail?id=${encodeURIComponent(lead.crmClientId)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir cliente CRM
            </a>
          )}
          {lead.stage === 'won' && !lead.crmClientId && (
            <button className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} onClick={openTransfer}>
              Convertir a cliente
            </button>
          )}
          <button className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={edit.editing ? cancelEdit : startEdit}>
            {edit.editing ? 'Cancelar' : 'Editar'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className={styles.leadDetail}>
        <div className={styles.main}>
          {/* Edit Form */}
          {edit.editing && (
            <EditForm form={edit.form} onChange={editInput} onSave={saveEdit} onCancel={cancelEdit} />
          )}

          {/* Stage */}
          <div className={styles.detailSection}>
            <div className={styles.sectionHeader}>Stage</div>
            <div className={styles.sectionBody}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                <span className={styles.stageBadge} style={{ background: STAGE_COLORS[lead.stage] || '#6366f1' }}>
                  {stageLabel(lead.stage)}
                </span>
                <span className={styles.textMuted}>desde {formatDate(lead.updatedAt)}</span>
              </div>
              {transitions.length > 0 && (
                <div className={styles.transitions}>
                  {transitions.map((s) => (
                    <button key={s} className={styles.transitionBtn} onClick={() => transitionStage(s)}>
                      {stageLabel(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Identidad */}
          <FieldSection title="Identidad" fields={[
            { label: 'Empresa', value: lead.companyName },
            { label: 'Contacto', value: lead.contactName },
            { label: 'Titulo', value: lead.contactTitle },
            { label: 'Email', value: lead.contactEmail, href: lead.contactEmail ? `mailto:${lead.contactEmail}` : undefined },
            { label: 'Telefono', value: lead.contactPhone, href: lead.contactPhone ? `tel:${lead.contactPhone}` : undefined },
            { label: 'Website', value: lead.websiteUrl, href: lead.websiteUrl || undefined },
          ]} />

          {/* Clasificacion */}
          <FieldSection title="Clasificacion" fields={[
            { label: 'Sector', value: lead.sector },
            { label: 'Subsector', value: lead.subsector },
            { label: 'Tier', value: lead.tier, badge: lead.tier },
            { label: 'Region', value: lead.region },
          ]} />

          {/* Scoring */}
          <div className={styles.detailSection}>
            <div className={styles.sectionHeader}>Scoring</div>
            <div className={styles.sectionBody}>
              <div className={styles.fieldGrid}>
                <div className={styles.fieldItem}>
                  <div className={styles.fieldLabel}>Score Compuesto</div>
                  <div className={styles.fieldValue}>
                    <div className={styles.scoreBar}>
                      <div className={styles.scoreTrack}>
                        <div className={styles.scoreFill} style={{ width: `${Math.min(100, Math.max(0, compositeScore))}%`, background: scoreBarColor(compositeScore) }} />
                      </div>
                      <span className={styles.scoreValue} style={{ color: scoreBarColor(compositeScore) }}>{compositeScore}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.fieldItem}>
                  <div className={styles.fieldLabel}>Fit Score</div>
                  <div className={styles.fieldValue}>{lead.fitScore ?? '-'}</div>
                </div>
                <div className={styles.fieldItem}>
                  <div className={styles.fieldLabel}>Readiness</div>
                  <div className={styles.fieldValue}>{lead.readinessScore ?? '-'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Inteligencia */}
          <FieldSection title="Inteligencia" fields={[
            { label: 'Pain Point', value: lead.painPoint },
            { label: 'Angulo Comercial', value: lead.commercialAngle },
            { label: 'USP Verificado', value: lead.uspVerified },
            { label: 'Producto Estrella', value: lead.productStar },
            { label: 'Calidad Website', value: lead.websiteQuality != null ? `${lead.websiteQuality}/10` : null },
          ]} />

          {/* Datos Fiscales */}
          <FieldSection title="Datos Fiscales" fields={[
            { label: 'NIF / CIF / NIE', value: lead.taxId ? `${lead.taxId}${lead.taxIdType ? ` (${lead.taxIdType})` : ''}` : null },
            { label: 'Razon Social', value: lead.legalName },
            { label: 'Direccion Fiscal', value: lead.addressLine1 },
            { label: 'Ciudad', value: lead.city },
            { label: 'Codigo Postal', value: lead.postalCode },
            { label: 'Pais', value: lead.country },
          ]} />

          {/* Outreach */}
          <FieldSection title="Outreach" fields={[
            { label: 'Outreach Count', value: lead.outreachCount != null ? String(lead.outreachCount) : '0' },
            { label: 'Estado de Respuesta', value: RESPONSE_LABELS[lead.responseStatus || ''] || lead.responseStatus || 'Sin respuesta' },
            { label: 'Ultimo Contacto', value: lead.lastContactAt ? formatDate(lead.lastContactAt) : null },
            { label: 'Proximo Follow-up', value: lead.nextFollowupAt ? formatDate(lead.nextFollowupAt) : null },
          ]} />

          {/* Activity Timeline */}
          <ActivityTimeline activities={page.activities} />

          {/* Score History */}
          <ScoreHistory scores={page.scores} />
        </div>

        {/* Sidebar */}
        <div className={styles.sidebar}>
          {/* AI Actions */}
          <div className={styles.detailSection}>
            <div className={styles.sectionHeader}>Acciones IA</div>
            <div className={styles.sectionBody} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <button className={styles.llmBtn} onClick={runEnrich} disabled={llm.loading === 'enrich'}>
                <span className={styles.llmIcon}>&#128270;</span>
                <span className={styles.llmLabel}>
                  {llm.loading === 'enrich' ? 'Enriqueciendo...' : 'Enriquecer con IA'}
                  <span className={styles.llmDesc}>{llm.loading === 'enrich' ? 'Consultando IA' : 'Generar sugerencias de datos'}</span>
                </span>
              </button>
              <button className={styles.llmBtn} onClick={runOutreach} disabled={llm.loading === 'outreach'}>
                <span className={styles.llmIcon}>&#9993;</span>
                <span className={styles.llmLabel}>
                  {llm.loading === 'outreach' ? 'Generando...' : 'Generar Outreach'}
                  <span className={styles.llmDesc}>{llm.loading === 'outreach' ? 'Creando borrador' : 'Crear borrador de email'}</span>
                </span>
              </button>
              <button className={styles.llmBtn} onClick={runFollowup} disabled={llm.loading === 'followup'}>
                <span className={styles.llmIcon}>&#128197;</span>
                <span className={styles.llmLabel}>
                  {llm.loading === 'followup' ? 'Analizando...' : 'Sugerir Follow-up'}
                  <span className={styles.llmDesc}>{llm.loading === 'followup' ? 'Evaluando siguiente paso' : 'Proxima accion recomendada'}</span>
                </span>
              </button>
            </div>
          </div>

          {/* LLM Results */}
          {llm.result && (
            <LlmPanel
              result={llm.result}
              leadStage={lead.stage}
              onApply={applyEnrichField}
              onApplyAll={applyAllEnrich}
              onCopy={copyOutreach}
              onClose={closeLlmResult}
              onTransition={transitionStage}
            />
          )}

          {/* Metadata */}
          <div className={styles.detailSection}>
            <div className={styles.sectionBody}>
              <p className={styles.meta}>Creado: {formatDate(lead.createdAt)}</p>
              <p className={styles.meta}>Actualizado: {formatDate(lead.updatedAt)}</p>
              <p className={styles.meta} style={{ marginTop: 'var(--space-2)' }}>ID: {lead.id}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Transfer Modal */}
      {transfer.open && (
        <TransferModal
          transfer={transfer}
          lead={lead}
          onClose={closeTransfer}
          onChangeFiscal={updateFiscal}
          onSetStep={setTransferStep}
          onSubmit={submitTransfer}
        />
      )}
    </>
  );
}

function EditForm({ form, onChange, onSave, onCancel }: { form: Record<string, string>; onChange: (n: string, v: string) => void; onSave: () => void; onCancel: () => void }) {
  return (
    <div className={styles.detailSection}>
      <div className={styles.sectionHeaderFlex}>
        <span>Editar Lead</span>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} onClick={onSave}>Guardar</button>
          <button className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`} onClick={onCancel}>Cancelar</button>
        </div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.editGrid}>
          <EditField label="Empresa" name="companyName" value={form.companyName || ''} onChange={onChange} />
          <EditField label="Contacto" name="contactName" value={form.contactName || ''} onChange={onChange} />
          <EditField label="Titulo" name="contactTitle" value={form.contactTitle || ''} onChange={onChange} />
          <EditField label="Email" name="contactEmail" value={form.contactEmail || ''} onChange={onChange} />
          <EditField label="Telefono" name="contactPhone" value={form.contactPhone || ''} onChange={onChange} />
          <EditField label="Website" name="websiteUrl" value={form.websiteUrl || ''} onChange={onChange} />
          <EditSelect label="Sector" name="sector" value={form.sector || ''} onChange={onChange} options={['Tecnologia','Finanzas','Salud','Educacion','Retail','Manufactura','Servicios','Otros']} />
          <EditField label="Subsector" name="subsector" value={form.subsector || ''} onChange={onChange} />
          <EditSelect label="Tier" name="tier" value={form.tier || ''} onChange={onChange} options={['T1','T2','T3']} />
          <EditField label="Region" name="region" value={form.region || ''} onChange={onChange} />
          <EditSelect label="Fit Score" name="fitScore" value={form.fitScore || ''} onChange={onChange} options={['1','2','3','4','5']} />
          <EditSelect label="Readiness" name="readinessScore" value={form.readinessScore || ''} onChange={onChange} options={['Alta','Media','Baja']} />
          <EditField label="Pain Point" name="painPoint" value={form.painPoint || ''} onChange={onChange} textarea />
          <EditField label="Angulo Comercial" name="commercialAngle" value={form.commercialAngle || ''} onChange={onChange} textarea />
          <EditField label="USP Verificado" name="uspVerified" value={form.uspVerified || ''} onChange={onChange} textarea />
          <EditField label="Producto Estrella" name="productStar" value={form.productStar || ''} onChange={onChange} />
          <EditSelect label="Response Status" name="responseStatus" value={form.responseStatus || ''} onChange={onChange} options={['none','opened','replied','meeting_booked','declined']} />
          <EditField label="Notas" name="notes" value={form.notes || ''} onChange={onChange} textarea />
        </div>
        <div className={styles.sectionHeader} style={{ marginTop: 'var(--space-4)' }}>Datos Fiscales</div>
        <div className={styles.editGrid}>
          <EditField label="NIF / CIF / NIE" name="taxId" value={form.taxId || ''} onChange={onChange} />
          <EditSelect label="Tipo fiscal" name="taxIdType" value={form.taxIdType || ''} onChange={onChange} options={['NIF','CIF','NIE']} />
          <EditField label="Razon social" name="legalName" value={form.legalName || ''} onChange={onChange} />
          <EditField label="Direccion" name="addressLine1" value={form.addressLine1 || ''} onChange={onChange} />
          <EditField label="Ciudad" name="city" value={form.city || ''} onChange={onChange} />
          <EditField label="Codigo postal" name="postalCode" value={form.postalCode || ''} onChange={onChange} />
          <EditField label="Pais" name="country" value={form.country || ''} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}

function TransferModal({ transfer, lead, onClose, onChangeFiscal, onSetStep, onSubmit }: {
  transfer: TransferState;
  lead: LeadWithIntelligence;
  onClose: () => void;
  onChangeFiscal: (next: Partial<TransferState['fiscal']>) => void;
  onSetStep: (step: number) => void;
  onSubmit: (e: React.FormEvent) => void;
}) {
  return (
    <div
      className={styles.modalOverlay}
      role="button"
      tabIndex={0}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className={styles.modalDialog} role="dialog" aria-modal="true" aria-labelledby="transfer-title">
        <div className={styles.modalHead}>
          <div>
            <p className={styles.modalKicker}>CRM</p>
            <h2 id="transfer-title" className={styles.modalTitle}>Convertir a cliente</h2>
          </div>
          <button className={styles.modalClose} onClick={onClose} aria-label="Cerrar">&times;</button>
        </div>
        <form onSubmit={onSubmit}>
          <div className={styles.modalBody}>
            {transfer.step === 1 && (
              <div className={styles.editGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="transfer-taxId" className={styles.formLabel}>NIF/CIF/NIE</label>
                  <input id="transfer-taxId" className={styles.formInput} value={transfer.fiscal.taxId} onChange={(e) => onChangeFiscal({ taxId: e.target.value })} onBlur={(e) => onChangeFiscal({ taxId: e.target.value.trim().toUpperCase(), taxIdType: detectTaxIdType(e.target.value) })} />
                </div>
                <div className={styles.formGroup}>
                  <span className={styles.formLabel}>Tipo <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{transfer.fiscal.taxIdType || 'Sin identificar'}</span></span>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="transfer-legalName" className={styles.formLabel}>Razon social</label>
                  <input id="transfer-legalName" className={styles.formInput} value={transfer.fiscal.legalName} onChange={(e) => onChangeFiscal({ legalName: e.target.value })} />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="transfer-address" className={styles.formLabel}>Direccion</label>
                  <input id="transfer-address" className={styles.formInput} value={transfer.fiscal.addressLine1} onChange={(e) => onChangeFiscal({ addressLine1: e.target.value })} />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="transfer-city" className={styles.formLabel}>Ciudad</label>
                  <input id="transfer-city" className={styles.formInput} value={transfer.fiscal.city} onChange={(e) => onChangeFiscal({ city: e.target.value })} />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="transfer-postal" className={styles.formLabel}>Codigo postal</label>
                  <input id="transfer-postal" className={styles.formInput} value={transfer.fiscal.postalCode} onChange={(e) => onChangeFiscal({ postalCode: e.target.value })} />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="transfer-country" className={styles.formLabel}>Pais</label>
                  <input id="transfer-country" className={styles.formInput} maxLength={2} value={transfer.fiscal.country} onChange={(e) => onChangeFiscal({ country: e.target.value.toUpperCase() })} />
                </div>
              </div>
            )}
            {transfer.step === 2 && (
              <div>
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <p><strong>Lead:</strong> {lead.companyName}</p>
                  <p><strong>Email:</strong> {lead.contactEmail || ''}</p>
                  <p><strong>Fiscal:</strong> {transfer.fiscal.taxId || 'Sin NIF/CIF/NIE'} {transfer.fiscal.taxIdType}</p>
                  <p><strong>Razon social:</strong> {transfer.fiscal.legalName || lead.companyName}</p>
                  <p><strong>Direccion:</strong> {[transfer.fiscal.addressLine1, transfer.fiscal.postalCode, transfer.fiscal.city, transfer.fiscal.country].filter(Boolean).join(', ')}</p>
                </div>
                {transfer.result && <div className={styles.llmResult} role="status">{transfer.result}</div>}
              </div>
            )}
          </div>
          <div className={styles.modalActions}>
            {transfer.step === 1 && (
              <>
                <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onClose}>Cancelar</button>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => onSetStep(2)}>Continuar</button>
              </>
            )}
            {transfer.step === 2 && (
              <>
                <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={() => onSetStep(1)}>Volver</button>
                <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={transfer.loading}>
                  {transfer.loading ? 'Transfiriendo...' : 'Transferir lead'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function LlmPanel({ result, leadStage, onApply, onApplyAll, onCopy, onClose, onTransition }: {
  result: LlmResult;
  leadStage: string;
  onApply: (f: string, v: string) => void;
  onApplyAll: () => void;
  onCopy: () => void;
  onClose: () => void;
  onTransition: (s: string) => void;
}) {
  return (
    <div className={styles.detailSection}>
      <div className={styles.sectionHeaderFlex}>
        <span>
          {result.type === 'enrich' && 'Resultados de Enriquecimiento'}
          {result.type === 'outreach' && 'Borrador de Outreach'}
          {result.type === 'followup' && 'Sugerencia de Follow-up'}
        </span>
        <button className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`} onClick={onClose}>Cerrar</button>
      </div>
      <div className={styles.sectionBody}>
        {result.type === 'enrich' && (
          <EnrichPanel data={result.data} onApply={onApply} onApplyAll={onApplyAll} />
        )}
        {result.type === 'outreach' && (
          <OutreachPanel data={result.data} onCopy={onCopy} onClose={onClose} />
        )}
        {result.type === 'followup' && (
          <FollowupPanel data={result.data} leadStage={leadStage} onTransition={onTransition} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function ActivityTimeline({ activities }: { activities: LeadActivity[] }) {
  return (
    <div className={styles.detailSection}>
      <div className={styles.sectionHeader}>Actividades ({activities.length})</div>
      <div className={styles.sectionBody}>
        {activities.length === 0 ? (
          <p className={styles.textMuted}>Sin actividades registradas.</p>
        ) : (
          activities.map((act) => (
            <div key={act.id} className={styles.activityRow}>
              <div className={styles.activityIcon}>{ACTIVITY_ICONS[act.type] || 'Nota'}</div>
              <div className={styles.activityContent}>
                <div className={styles.activityText}>{act.content || act.type}</div>
                <div className={styles.activityMeta}>{act.performedBy || ''} &middot; {timeAgo(act.createdAt)}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ScoreHistory({ scores }: { scores: LeadScore[] }) {
  if (scores.length === 0) return null;
  return (
    <div className={styles.detailSection}>
      <div className={styles.sectionHeader}>Historial de Scores</div>
      <div className={styles.sectionBody} style={{ padding: 0 }}>
        <table className={styles.scoreTable}>
          <thead>
            <tr>
              <th>Score</th><th>Tier</th><th>Fit</th><th>Readiness</th><th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {scores.map((s) => (
              <tr key={s.id}>
                <td><span className={`${styles.scoreValue} ${scoreClass(s.compositeScore)}`}>{s.compositeScore ?? '-'}</span></td>
                <td>{s.tier || '-'}</td>
                <td>{s.fitScore ?? '-'}</td>
                <td>{s.readinessScore || '-'}</td>
                <td>{formatDate(s.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Sub-components */

function EditField({ label, name, value, onChange, textarea }: { label: string; name: string; value: string; onChange: (n: string, v: string) => void; textarea?: boolean }) {
  const isFull = ['painPoint','commercialAngle','uspVerified','notes'].includes(name);
  return (
    <div className={`${styles.formGroup} ${isFull ? styles.formGroupFull : ''}`}>
      <label className={styles.formLabel} htmlFor={`edit-${name}`}>{label}</label>
      {textarea ? (
        <textarea id={`edit-${name}`} className={styles.formInput} value={value} onChange={(e) => onChange(name, e.target.value)} />
      ) : (
        <input id={`edit-${name}`} type="text" className={styles.formInput} value={value} onChange={(e) => onChange(name, e.target.value)} />
      )}
    </div>
  );
}

function EditSelect({ label, name, value, onChange, options }: { label: string; name: string; value: string; onChange: (n: string, v: string) => void; options: string[] }) {
  return (
    <div className={styles.formGroup}>
      <label className={styles.formLabel} htmlFor={`edit-${name}`}>{label}</label>
      <select id={`edit-${name}`} className={styles.formInput} value={value} onChange={(e) => onChange(name, e.target.value)}>
        <option value="">-</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function FieldSection({ title, fields }: { title: string; fields: { label: string; value?: string | null; href?: string; badge?: string | null }[] }) {
  return (
    <div className={styles.detailSection}>
      <div className={styles.sectionHeader}>{title}</div>
      <div className={styles.sectionBody}>
        <div className={styles.fieldGrid}>
          {fields.map((f) => {
            const hasValue = f.value != null && f.value !== '';
            const isWide = f.label === 'Pain Point' || f.label === 'Angulo Comercial' || f.label === 'USP Verificado';
            return (
              <div key={f.label} className={styles.fieldItem} style={isWide ? { gridColumn: '1 / -1' } : undefined}>
                <div className={styles.fieldLabel}>{f.label}</div>
                <div className={`${styles.fieldValue} ${!hasValue ? styles.textMuted : ''}`}>
                  {f.badge && hasValue ? (
                    <span className={`${styles.tierBadge} ${f.badge === 'T1' ? styles.tierT1 : f.badge === 'T2' ? styles.tierT2 : styles.tierT3}`}>{f.badge}</span>
                  ) : f.href && hasValue ? (
                    <a href={f.href} target={f.href.startsWith('http') ? '_blank' : undefined} rel={f.href.startsWith('http') ? 'noopener noreferrer' : undefined}>{f.value}</a>
                  ) : (
                    hasValue ? f.value : '---'
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EnrichPanel({ data, onApply, onApplyAll }: { data: EnrichResult; onApply: (f: string, v: string) => void; onApplyAll: () => void }) {
  const enrichable = [
    'companyName','contactName','contactTitle','contactEmail','sector','subsector','region',
    'painPoint','commercialAngle','uspVerified','productStar','websiteQuality',
    'taxId','taxIdType','legalName','addressLine1','city','postalCode','country',
  ];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>Confianza: {data.confidence || 'media'}</span>
      </div>
      {enrichable.map((field) => {
        const v = data[field];
        if (v == null || v === '') return null;
        return (
          <div key={field} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-3)', padding: '0.45rem 0', borderBottom: '1px solid var(--border-light)', alignItems: 'center' }}>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{FIELD_LABELS[field] || field}</div>
            <div style={{ fontSize: 'var(--text-sm)' }}>{String(v)}</div>
            <button className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={() => onApply(field, String(v))}>Aplicar</button>
          </div>
        );
      })}
      <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-2)' }}>
        <button className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} onClick={onApplyAll}>Aplicar Todo</button>
      </div>
    </div>
  );
}

function OutreachPanel({ data, onCopy, onClose }: { data: OutreachResult; onCopy: () => void; onClose: () => void }) {
  return (
    <div>
      <div style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>Template: {data.templateUsed || 'default'}</div>
      <div style={{ background: 'var(--accent-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 'var(--space-4)', fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <div style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>Asunto: {data.subject || ''}</div>
        <div>{data.body || ''}</div>
        {data.personalizationNotes && (
          <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--border-light)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>Notas: {data.personalizationNotes}</div>
        )}
      </div>
      <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-2)' }}>
        <button className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} onClick={onCopy}>Copiar Email</button>
        <button className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`} onClick={onClose}>Cerrar</button>
      </div>
    </div>
  );
}

function FollowupPanel({ data, leadStage, onTransition, onClose }: { data: FollowupResult; leadStage: string; onTransition: (s: string) => void; onClose: () => void }) {
  const canTransition = data.suggestedStage && TRANSITIONS[leadStage]?.includes(data.suggestedStage);
  return (
    <div>
      <div style={{ background: 'var(--accent-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 'var(--space-4)' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>{data.action || ''}</div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{data.reasoning || ''}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          Urgencia: {data.urgency || 'media'}
          {data.suggestedStage && <span>Stage sugerido: {stageLabel(data.suggestedStage)}</span>}
        </div>
      </div>
      {canTransition && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <button className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} onClick={() => onTransition(data.suggestedStage!)}>
            Mover a {stageLabel(data.suggestedStage!)}
          </button>
        </div>
      )}
      <div style={{ marginTop: 'var(--space-3)' }}>
        <button className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`} onClick={onClose}>Cerrar</button>
      </div>
    </div>
  );
}
